import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link, Route, Switch, useLocation } from "wouter";
import { AlertTriangle, Loader2, Upload } from "lucide-react";
import { api, type Me, type Status } from "./lib/api";
import { ChangePassword, Login, Setup, UserMenu } from "./components/Auth";
import { SettingsPage } from "./routes/Settings";
import { StatsPage } from "./routes/Stats";
import { Library } from "./routes/Library";
import { AssetView } from "./routes/AssetView";
import { TagRegistry } from "./routes/TagRegistry";
import { EmbedGuide } from "./routes/EmbedGuide";

type Ctx = { status: Status | null; user: Me | null; isAdmin: boolean; upload: (files: File[]) => void; version: number; bump: () => void };
const AppCtx = createContext<Ctx>({ status: null, user: null, isAdmin: false, upload: () => {}, version: 0, bump: () => {} });
export const useApp = () => useContext(AppCtx);

type UploadResult = { id?: string; filename: string; duplicate?: { id: string; title: string }; error?: string };

type Session = { user: Me | null; setup: boolean } | null;

export function App() {
  const [session, setSession] = useState<Session>(null);
  const loadSession = useCallback(() => {
    fetch("/api/me").then((r) => r.json()).then((d) => setSession({ user: d.user ?? null, setup: !!d.setup }))
      .catch(() => setSession({ user: null, setup: false }));
  }, []);
  useEffect(() => {
    loadSession();
    const out = () => setSession((s) => (s?.user ? { user: null, setup: false } : s));
    window.addEventListener("bildbanken:logged-out", out);
    return () => window.removeEventListener("bildbanken:logged-out", out);
  }, [loadSession]);

  if (!session) return null;
  if (!session.user) return session.setup ? <Setup onDone={loadSession} /> : <Login onDone={loadSession} />;
  if (session.user.mustChange) return <ChangePassword forced onDone={loadSession} />;
  return <Shell user={session.user} />;
}

function Shell({ user }: { user: Me }) {
  const isAdmin = user.role === "admin";
  const [status, setStatus] = useState<Status | null>(null);
  const [version, setVersion] = useState(0);
  const [uploading, setUploading] = useState(0);
  const [results, setResults] = useState<UploadResult[] | null>(null);
  const [dragging, setDragging] = useState(false);
  const [, navigate] = useLocation();

  useEffect(() => { api<Status>("status").then(setStatus).catch(() => {}); }, []);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const upload = useCallback(async (files: File[]) => {
    const imgs = files.filter((f) => f.type.startsWith("image/"));
    if (!imgs.length || !isAdmin) return;
    setUploading(imgs.length);
    setResults(null);
    const fd = new FormData();
    imgs.forEach((f) => fd.append("files", f));
    try {
      const r = await api<{ results: UploadResult[] }>("assets", { method: "POST", body: fd });
      setResults(r.results);
      bump();
      const created = r.results.filter((x) => x.id);
      if (created.length === 1) navigate(`/bild/${created[0]!.id}`);
      else navigate("/");
    } catch (e) {
      setResults([{ filename: "Uppladdningen", error: e instanceof Error ? e.message : "misslyckades" }]);
    } finally {
      setUploading(0);
    }
  }, [bump, navigate]);

  // Släpp bilder var som helst i fönstret (bara admin laddar upp).
  useEffect(() => {
    if (!isAdmin) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes("Files");
    const enter = (e: DragEvent) => { if (hasFiles(e)) { depth++; setDragging(true); } };
    const leave = () => { depth = Math.max(0, depth - 1); if (!depth) setDragging(false); };
    const over = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); depth = 0; setDragging(false);
      // Släpp på "Ersätt fil"-ytan hanteras där – inte här.
      if ((e.target as HTMLElement).closest("[data-replace-drop]")) return;
      upload([...(e.dataTransfer?.files ?? [])]);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
    };
  }, [upload, isAdmin]);

  return (
    <AppCtx.Provider value={{ status, user, isAdmin, upload, version, bump }}>
      <Header uploading={uploading} />
      <main className="mx-auto max-w-[1500px] px-4 pb-24 sm:px-6">
        <Switch>
          <Route path="/" component={Library} />
          <Route path="/bild/:id">{(p) => <AssetView id={p.id} />}</Route>
          {isAdmin && <Route path="/taggar" component={TagRegistry} />}
          {isAdmin && <Route path="/installningar" component={SettingsPage} />}
          {isAdmin && <Route path="/statistik" component={StatsPage} />}
          <Route path="/badda-in" component={EmbedGuide} />
          <Route path="/konto"><ChangePassword onDone={() => history.back()} /></Route>
          <Route>Sidan finns inte.</Route>
        </Switch>
      </main>
      {results && <UploadReport results={results} onClose={() => setResults(null)} />}
      {dragging && (
        <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center bg-ember/10 backdrop-blur-[2px]">
          <div className="rounded-2xl border-2 border-dashed border-ember bg-card px-10 py-8 text-center shadow-xl">
            <Upload className="mx-auto mb-2 text-ember" size={28} />
            <div className="text-lg font-semibold">Släpp för att ladda upp</div>
            <div className="text-soft">Bilderna analyseras och får taggförslag automatiskt</div>
          </div>
        </div>
      )}
    </AppCtx.Provider>
  );
}

function Header({ uploading }: { uploading: number }) {
  const { upload, isAdmin, user, status } = useApp();
  // Admin ska se direkt om Mac-agenten är nere medan bilder väntar på analys.
  const agentDown = isAdmin && status?.ai.visionMode === "agent" && status.agent && !status.agent.online && status.agent.waiting > 0;
  return (
    <header className="sticky top-0 z-40 border-b border-line bg-paper/90 backdrop-blur">
      <div className="mx-auto flex max-w-[1500px] items-center gap-6 px-4 py-3 sm:px-6">
        <Link href="/" className="flex items-center gap-2.5">
          <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden>
            <rect width="32" height="32" rx="7" fill="#C2410C" />
            <path d="M7 23l6-7 4 4 3-3 5 6z" fill="white" /><circle cx="21" cy="11" r="2.6" fill="white" />
          </svg>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold tracking-tight">Bildbanken</div>
            <div className="hidden whitespace-nowrap text-[11px] text-muted sm:block">Sölvesborg-Mjällby Sparbank</div>
          </div>
        </Link>
        <div className="flex-1" />
        {agentDown && (
          <Link href="/installningar" className="hidden items-center gap-1.5 rounded-lg bg-warn-soft px-2.5 py-1 text-[12.5px] font-medium text-warn lg:flex">
            <AlertTriangle size={13} /> Mac-agenten är offline · {status!.agent!.waiting} väntar
          </Link>
        )}
        {isAdmin && <label className="btn-primary cursor-pointer whitespace-nowrap">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {uploading ? `Laddar upp ${uploading}…` : "Ladda upp"}
          <input type="file" accept="image/*" multiple hidden onChange={(e) => { upload([...(e.target.files ?? [])]); e.target.value = ""; }} />
        </label>}
        {user && <UserMenu user={user} />}
      </div>
    </header>
  );
}

function UploadReport({ results, onClose }: { results: UploadResult[]; onClose: () => void }) {
  const ok = results.filter((r) => r.id).length;
  const dupes = results.filter((r) => r.duplicate);
  const errs = results.filter((r) => r.error);
  useEffect(() => {
    if (dupes.length || errs.length) return;
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] card p-4 shadow-xl">
      <div className="mb-1 flex items-center justify-between">
        <div className="font-semibold">{ok} bild{ok === 1 ? "" : "er"} uppladdad{ok === 1 ? "" : "e"}</div>
        <button onClick={onClose} className="text-muted hover:text-ink">Stäng</button>
      </div>
      {ok > 0 && <p className="text-soft">Analyseras nu – taggförslag dyker upp på bilderna inom några sekunder.</p>}
      {dupes.map((d) => (
        <p key={d.filename} className="mt-2 rounded-lg bg-warn-soft px-2.5 py-1.5 text-warn">
          <b>{d.filename}</b> finns redan: <Link href={`/bild/${d.duplicate!.id}`} className="underline" onClick={onClose}>{d.duplicate!.title || "öppna"}</Link>
        </p>
      ))}
      {errs.map((d) => <p key={d.filename} className="mt-2 rounded-lg bg-bad-soft px-2.5 py-1.5 text-bad"><b>{d.filename}</b>: {d.error}</p>)}
    </div>
  );
}
